import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BaseService } from '@/core/api/base-service';
import { ApiConfiguration } from '@/core/api/api-configuration';
import {
    ComissaoConvocacaoContextoRequest,
    ComissaoConvocacaoEmailEnvioResponse,
    ComissaoConvocacaoEmailParticipanteResponse,
    ComissaoConvocacaoEmailRequest,
    ComissaoConvocacaoGerenciarResponse,
    ComissaoConvocacaoMarcarTodosRequest,
    ComissaoConvocacaoParticipanteRequest,
    ComissaoAtaAutosaveConfigResponse,
    ComissaoAtaConvidadoRequest,
    ComissaoAtaFinalizacaoTextoResponse,
    ComissaoAtaConvidadoResponse,
    ComissaoAtaImportanteRequest,
    ComissaoAtaInserirModeloRequest,
    ComissaoAtaInicioTextoResponse,
    ComissaoAtaModeloDocumentoResponse,
    ComissaoAtaModeloResponse,
    ComissaoAtaParticipanteRequest,
    ComissaoAtaProtocoloResponse,
    ComissaoAtaSalvarRequest,
    ComissaoAtaSalvarResponse,
    ComissaoAtaStatusRequest,
    ComissaoAssistenteFotoResponse,
    ComissaoAssistenteResponse,
    ComissaoReuniaoAssistenteRequest,
    ComissaoReuniaoDocumentoHtmlResponse,
    ComissaoEstruturaFiltroResponse,
    ComissaoLocalReuniaoResponse,
    ComissaoReuniaoFiltroRequest,
    ComissaoReuniaoMembroResponse,
    ComissaoReuniaoPageResponse,
    ComissaoReuniaoSalvarRequest,
    ComissaoReuniaoSalvarResponse,
    ComissaoStatusAtaResponse,
    ComissaoTipoComissaoResponse,
    ComissaoReuniaoDashboardSummaryResponse
} from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.model';
import { environment } from '@env/environment';

import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ComissaoReuniaoService extends BaseService {
    readonly PATH = environment.basePath + '/comissoes/reunioes';
    readonly PATH_ATAS = environment.basePath + '/comissoes/atas';

    constructor(config: ApiConfiguration, http: HttpClient) {
        super(config, http, environment.apis.apiSicom);
    }

    consultarReunioes(request: ComissaoReuniaoFiltroRequest, page: number = 0, size: number = 200, context?: HttpContext): Observable<ComissaoReuniaoPageResponse> {
        const params = new HttpParams().set('page', String(page)).set('size', String(size));
        return this.http.post<ComissaoReuniaoPageResponse>(`${this.PATH}/consultar`, request, { params, context });
    }

    consultarDashboardSummary(request: ComissaoReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoReuniaoDashboardSummaryResponse> {
        return this.http.post<ComissaoReuniaoDashboardSummaryResponse>(`${this.PATH}/dashboard-summary`, request, { context });
    }

    cadastrarReuniao(request: ComissaoReuniaoSalvarRequest, context?: HttpContext): Observable<ComissaoReuniaoSalvarResponse> {
        return this.http.post<ComissaoReuniaoSalvarResponse>(this.PATH, request, { context });
    }

    atualizarReuniao(idReuniao: number, request: ComissaoReuniaoSalvarRequest, context?: HttpContext): Observable<ComissaoReuniaoSalvarResponse> {
        return this.http.put<ComissaoReuniaoSalvarResponse>(`${this.PATH}/${idReuniao}`, request, { context });
    }

    consultarConvocacoes(idReuniao: number, context?: HttpContext): Observable<ComissaoConvocacaoGerenciarResponse> {
        return this.http.get<ComissaoConvocacaoGerenciarResponse>(`${this.PATH}/${idReuniao}/convocacoes`, { context });
    }

    salvarContextoConvocacao(idReuniao: number, request: ComissaoConvocacaoContextoRequest, context?: HttpContext): Observable<void> {
        return this.http.put<void>(`${this.PATH}/${idReuniao}/convocacoes/contexto`, request, { context });
    }

    atualizarParticipanteConvocacao(idReuniao: number, request: ComissaoConvocacaoParticipanteRequest, context?: HttpContext): Observable<void> {
        return this.http.put<void>(`${this.PATH}/${idReuniao}/convocacoes/participante`, request, { context });
    }

    marcarTodosConvocacao(idReuniao: number, request: ComissaoConvocacaoMarcarTodosRequest, context?: HttpContext): Observable<void> {
        return this.http.put<void>(`${this.PATH}/${idReuniao}/convocacoes/marcar-todos`, request, { context });
    }

    removerConvidadoConvocacao(idReuniao: number, idSolicitante: number, ufSgUf: string, context?: HttpContext): Observable<void> {
        const params = new HttpParams().set('idSolicitante', idSolicitante).set('ufSgUf', ufSgUf);
        return this.http.delete<void>(`${this.PATH}/${idReuniao}/convocacoes/convidados`, { params, context });
    }

    enviarEmailsConvocacao(idReuniao: number, request: ComissaoConvocacaoEmailRequest, context?: HttpContext): Observable<ComissaoConvocacaoEmailEnvioResponse> {
        return this.http.post<ComissaoConvocacaoEmailEnvioResponse>(`${this.PATH}/${idReuniao}/convocacoes/email`, request, { context });
    }

    reenviarEmailConvocacao(idReuniao: number, idSolicitante: number, ufSgUf: string, context?: HttpContext): Observable<ComissaoConvocacaoEmailEnvioResponse> {
        return this.http.post<ComissaoConvocacaoEmailEnvioResponse>(`${this.PATH}/${idReuniao}/convocacoes/email/${idSolicitante}/${ufSgUf}/reenviar`, null, { context });
    }

    listarHistoricoEmailConvocacao(idReuniao: number, context?: HttpContext): Observable<ComissaoConvocacaoEmailParticipanteResponse[]> {
        return this.http.get<ComissaoConvocacaoEmailParticipanteResponse[]>(`${this.PATH}/${idReuniao}/convocacoes/email/historico`, { context });
    }

    baixarOficioConvite(idReuniao: number, idSolicitante: number, ufSgUf: string, context?: HttpContext): Observable<Blob> {
        return this.http.get(`${this.PATH}/${idReuniao}/convocacoes/oficio/${idSolicitante}/${ufSgUf}/documento`, { context, responseType: 'blob' });
    }

    consultarDocumentoAtaHtml(idReuniao: number, context?: HttpContext): Observable<ComissaoReuniaoDocumentoHtmlResponse> {
        return this.http.get<ComissaoReuniaoDocumentoHtmlResponse>(`${this.PATH_ATAS}/${idReuniao}/documento-html`, { context });
    }

    consultarPreviewAtaHtml(idReuniao: number, context?: HttpContext): Observable<ComissaoReuniaoDocumentoHtmlResponse> {
        return this.http.get<ComissaoReuniaoDocumentoHtmlResponse>(`${this.PATH_ATAS}/${idReuniao}/preview-html`, { context });
    }

    baixarDocumentoAta(idReuniao: number, context?: HttpContext): Observable<Blob> {
        return this.http.get(`${this.PATH_ATAS}/${idReuniao}/download`, { context, responseType: 'blob' });
    }

    listarModelosAta(context?: HttpContext): Observable<ComissaoAtaModeloResponse[]> {
        return this.http.get<ComissaoAtaModeloResponse[]>(`${this.PATH_ATAS}/modelos`, { context });
    }

    consultarModeloAtaHtml(idModeloAta: number, context?: HttpContext): Observable<ComissaoAtaModeloDocumentoResponse> {
        return this.http.get<ComissaoAtaModeloDocumentoResponse>(`${this.PATH_ATAS}/modelos/${idModeloAta}/documento-html`, { context });
    }

    inserirModeloAta(idReuniao: number, request: ComissaoAtaInserirModeloRequest, context?: HttpContext): Observable<void> {
        return this.http.post<void>(`${this.PATH_ATAS}/${idReuniao}/modelos`, request, { context });
    }

    consultarAutosaveAta(idEstruturaOrganizacional: number, context?: HttpContext): Observable<ComissaoAtaAutosaveConfigResponse> {
        const params = new HttpParams().set('idEstruturaOrganizacional', String(idEstruturaOrganizacional));
        return this.http.get<ComissaoAtaAutosaveConfigResponse>(`${this.PATH_ATAS}/autosave-config`, { params, context });
    }

    gerarProtocoloAta(idReuniao: number, context?: HttpContext): Observable<ComissaoAtaProtocoloResponse> {
        return this.http.post<ComissaoAtaProtocoloResponse>(`${this.PATH_ATAS}/${idReuniao}/protocolo`, null, { context });
    }

    salvarAta(idReuniao: number, request: ComissaoAtaSalvarRequest, context?: HttpContext): Observable<ComissaoAtaSalvarResponse> {
        return this.http.put<ComissaoAtaSalvarResponse>(`${this.PATH_ATAS}/${idReuniao}/documento`, request, { context });
    }

    cancelarReuniao(idReuniao: number, context?: HttpContext): Observable<void> {
        return this.http.put<void>(`${this.PATH}/${idReuniao}/cancelar`, null, { context });
    }

    excluirReuniao(idReuniao: number, context?: HttpContext): Observable<void> {
        return this.http.delete<void>(`${this.PATH}/${idReuniao}`, { context });
    }

    reativarReuniao(idReuniao: number, context?: HttpContext): Observable<void> {
        return this.http.put<void>(`${this.PATH}/${idReuniao}/reativar`, null, { context });
    }

    atualizarStatusAta(idReuniao: number, request: ComissaoAtaStatusRequest, context?: HttpContext): Observable<void> {
        return this.http.put<void>(`${this.PATH_ATAS}/${idReuniao}/status`, request, { context });
    }

    atualizarImportanteAta(idReuniao: number, request: ComissaoAtaImportanteRequest, context?: HttpContext): Observable<void> {
        return this.http.put<void>(`${this.PATH_ATAS}/${idReuniao}/importante`, request, { context });
    }

    atualizarParticipanteAta(idReuniao: number, request: ComissaoAtaParticipanteRequest, context?: HttpContext): Observable<void> {
        return this.http.put<void>(`${this.PATH_ATAS}/${idReuniao}/participantes`, request, { context });
    }

    adicionarConvidadoAta(idReuniao: number, request: ComissaoAtaConvidadoRequest, context?: HttpContext): Observable<void> {
        return this.http.post<void>(`${this.PATH_ATAS}/${idReuniao}/convidados`, request, { context });
    }

    removerConvidadoAta(idReuniao: number, idSolicitante: number, ufSgUf: string, context?: HttpContext): Observable<void> {
        return this.http.delete<void>(`${this.PATH_ATAS}/${idReuniao}/convidados/${idSolicitante}/${ufSgUf}`, { context });
    }

    atualizarAssistenteReuniao(idReuniao: number, request: ComissaoReuniaoAssistenteRequest, context?: HttpContext): Observable<void> {
        return this.http.put<void>(`${this.PATH}/${idReuniao}/assistente`, request, { context });
    }

    gerarTextoInicioAta(idReuniao: number, context?: HttpContext): Observable<ComissaoAtaInicioTextoResponse> {
        return this.http.get<ComissaoAtaInicioTextoResponse>(`${this.PATH_ATAS}/${idReuniao}/inicio`, { context });
    }

    gerarTextoFinalizacaoAta(idReuniao: number, context?: HttpContext): Observable<ComissaoAtaFinalizacaoTextoResponse> {
        return this.http.get<ComissaoAtaFinalizacaoTextoResponse>(`${this.PATH_ATAS}/${idReuniao}/finalizacao`, { context });
    }

    listarConvidadosAta(idReuniao: number, context?: HttpContext): Observable<ComissaoAtaConvidadoResponse[]> {
        return this.http.get<ComissaoAtaConvidadoResponse[]>(`${this.PATH_ATAS}/${idReuniao}/convidados`, { context });
    }

    listarMembrosReuniao(idReuniao: number, context?: HttpContext): Observable<ComissaoReuniaoMembroResponse[]> {
        return this.http.get<ComissaoReuniaoMembroResponse[]>(`${this.PATH}/${idReuniao}/membros`, { context });
    }

    listarStatusAta(context?: HttpContext): Observable<ComissaoStatusAtaResponse[]> {
        return this.http.get<ComissaoStatusAtaResponse[]>(`${this.PATH_ATAS}/status`, { context });
    }

    listarLocaisReuniao(context?: HttpContext): Observable<ComissaoLocalReuniaoResponse[]> {
        return this.http.get<ComissaoLocalReuniaoResponse[]>(`${this.PATH}/locais`, { context });
    }

    listarAssistentes(idEstruturaOrganizacional: number, context?: HttpContext): Observable<ComissaoAssistenteResponse[]> {
        const params = new HttpParams().set('idEstruturaOrganizacional', String(idEstruturaOrganizacional));
        return this.http.get<ComissaoAssistenteResponse[]>(`${this.PATH}/assistentes`, { params, context });
    }

    /** Foto do assistente (usuário/funcionário ou médico) para o avatar da tabela de acompanhamento. */
    obterFotoAssistente(idSolicitante: number, uf?: string | null, context?: HttpContext): Observable<ComissaoAssistenteFotoResponse> {
        let params = new HttpParams();
        if (uf) {
            params = params.set('uf', uf);
        }
        return this.http.get<ComissaoAssistenteFotoResponse>(`${this.PATH}/assistentes/${idSolicitante}/foto`, { params, context });
    }

    listarFiltrosComissao(idEstruturaOrganizacional: number, context?: HttpContext): Observable<ComissaoEstruturaFiltroResponse[]> {
        const params = new HttpParams().set('idEstruturaOrganizacional', String(idEstruturaOrganizacional));
        return this.http.get<ComissaoEstruturaFiltroResponse[]>(`${this.PATH}/filtros-comissao`, { params, context });
    }

    listarTiposComissao(idTipoComissaoCt?: number, context?: HttpContext): Observable<ComissaoTipoComissaoResponse[]> {
        let params = new HttpParams();
        if (typeof idTipoComissaoCt === 'number' && Number.isFinite(idTipoComissaoCt) && idTipoComissaoCt > 0) {
            params = params.set('idTipoComissaoCt', String(idTipoComissaoCt));
        }

        return this.http.get<ComissaoTipoComissaoResponse[]>(`${this.PATH}/tipos-comissao`, { params, context });
    }
}
